import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, doc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { createAdminWithAuth } from '../services/admin/adminAuthService';
import { getAdminCount, getPendingFarmersCount, getApprovedFarmersCount, getTotalPendingCount, getNewDiagnosticRequestsCount } from '../services/admin/adminService';

// Hook responsive
import useResponsive from './Map/ResponsiveLayout';

// Composants modulaires
import Sidebar from './admin/Sidebar';
import TopBar from './admin/TopBar';
import Dashboard from './admin/Dashboard';
import AdminRequests from './admin/AdminRequests';
import Administrators from './admin/Administrators';
import Farmers from './admin/Farmers';
import PendingFarmers from './admin/PendingFarmers';
import Projects from './admin/Projects';
import Settings from './admin/Settings';
import News from './admin/News';
import Statistics from './admin/Statistics';
import Announcements from './admin/Announcements'; // Nouvel import
import Diagnostics from './admin/Diagnostics';
import Newsletter from './admin/Newsletter';

/**
 * Tableau de bord administrateur principal
 * Gère l'état global et coordonne les différents composants modulaires
 */
const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedDiagnosticId, setSelectedDiagnosticId] = useState(null);
  const location = useLocation();

  // Masquer la navbar par défaut lorsqu'on est sur la page d'administration
  useEffect(() => {
    // Appliquer la couleur de fond pour les admins
    document.body.style.backgroundColor = '#f8f9fa';
    
    // Sélectionner la barre de navigation par défaut (blanche)
    const navbar = document.querySelector('nav');
    // Sélectionner l'AppBar supérieure avec le texte défilant (verte)
    const topBanner = document.querySelector('nav + div');
    
    if (location.pathname === '/admin-dashboard') {
      // Cacher la barre de navigation blanche
      if (navbar) navbar.style.display = 'none';
      // Cacher également la bannière supérieure avec le texte défilant
      if (topBanner) topBanner.style.display = 'none';
    }
    
    // Restaurer l'affichage lors du démontage du composant
    return () => {
      if (navbar) navbar.style.display = 'flex';
      if (topBanner) topBanner.style.display = 'block';
      document.body.style.backgroundColor = '';
    };
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const section = params.get('section');
    const targetId = params.get('targetId') || params.get('diagnosticId');

    if (section) {
      setActiveSection(section);
    } else if (targetId) {
      setActiveSection('diagnostics');
    }

    setSelectedDiagnosticId(targetId);
  }, [location.search]);
  const [stats, setStats] = useState({
    totalFarmers: 0,
    pendingVerifications: 0,
    newDiagnostics: 0,
    recentRegistrations: []
  });
  const [adminRequests, setAdminRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const isMobile = useResponsive(); // Utilisation du hook pour détecter si on est sur mobile

  // Vérifier si l'utilisateur est connecté
  useEffect(() => {
    if (!currentUser) {
      navigate('/admin-login');
    }
  }, [currentUser, navigate]);

  // Charger les statistiques et les demandes d'administrateurs
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer le nombre d'agriculteurs approuvés (pour le compteur principal)
        const approvedFarmers = await getApprovedFarmersCount();
        
        // Récupérer tous les utilisateurs en attente d'approbation (agriculteurs + administrateurs)
        const pendingVerifications = await getTotalPendingCount();
        
        // Récupérer les inscriptions récentes
        let recentRegistrations = [];
        try {
          const recentQuery = query(
            collection(db, 'farmers'),
            orderBy('createdAt', 'desc'),
            limit(5)
          );
          const recentSnapshot = await getDocs(recentQuery);
          recentRegistrations = recentSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (err) {
          console.error('Erreur lors de la récupération des inscriptions récentes:', err);
          // Continuer avec une liste vide
        }
        
        // Récupérer les demandes d'administrateurs en attente
        const adminRequestsQuery = query(
          collection(db, 'admin_requests'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        
        const adminRequestsSnapshot = await getDocs(adminRequestsQuery);
        
        // Traitement des dates pour les demandes d'administrateurs
        const adminRequestsList = adminRequestsSnapshot.docs.map(doc => {
          const data = doc.data();
          let createdAtDate;
          
          if (data.createdAt?.toDate) {
            createdAtDate = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAtDate = data.createdAt;
          } else if (typeof data.createdAt === 'string') {
            try {
              createdAtDate = new Date(data.createdAt);
            } catch (err) {
              createdAtDate = new Date();
            }
          } else {
            createdAtDate = new Date();
          }
          
          return {
            id: doc.id,
            ...data,
            createdAt: createdAtDate
          };
        });
        
        // Récupérer le nombre total d'administrateurs (admin + PDG)
        const adminCount = await getAdminCount();

        const newDiagnostics = await getNewDiagnosticRequestsCount();
        
        setStats({
          totalFarmers: approvedFarmers, // Uniquement les agriculteurs approuvés
          pendingVerifications,
          newDiagnostics,
          recentRegistrations,
          adminCount // Ajouter le nombre d'administrateurs
        });
        setAdminRequests(adminRequestsList);
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Impossible de charger les données. Veuillez réessayer. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  // États pour la gestion du poste de l'administrateur
  const [adminPosition, setAdminPosition] = useState('');
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [selectedAdminForApproval, setSelectedAdminForApproval] = useState(null);

  // Fonction pour ouvrir la boîte de dialogue de saisie du poste
  const openPositionDialog = (request) => {
    setSelectedAdminForApproval(request);
    setAdminPosition('');
    setPositionDialogOpen(true);
  };

  // Fonction pour approuver une demande d'administrateur
  const approveAdminRequest = async () => {
    if (!selectedAdminForApproval || !adminPosition.trim()) return;

    try {
      setLoading(true);
      setPositionDialogOpen(false);
      
      // Récupérer les informations de la demande
      const { email, password, name, id: requestId } = selectedAdminForApproval;
      
      // Utiliser le service d'authentification pour créer l'administrateur
      const result = await createAdminWithAuth(
        {
          email,
          password,
          name,
          position: adminPosition.trim(),
          requestId
        },
        auth.currentUser ? auth.currentUser.uid : null
      );
      
      if (result.success) {
        // Mettre à jour la liste des demandes
        setAdminRequests(prevRequests => 
          prevRequests.filter(req => req.id !== requestId)
        );
        
        const roleDisplay = result.role === 'PDG' ? 'PDG (Super Admin)' : 'administrateur';
        setSuccessMessage(`${name} a été approuvé avec succès en tant que ${roleDisplay} avec le poste: ${adminPosition}. Un compte d'authentification a été créé et l'utilisateur peut maintenant se connecter avec son email et mot de passe.`);
        setTimeout(() => setSuccessMessage(''), 7000);
      } else {
        throw new Error(result.error);
      }

    } catch (error) {
      console.error('Erreur lors de l\'approbation de la demande:', error);
      let errorMessage = 'Une erreur est survenue lors de l\'approbation de la demande.';
      
      // Gestion des erreurs spécifiques à Firestore et Authentication
      if (error.code === 'permission-denied') {
        errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires pour cette opération.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service indisponible. Veuillez vérifier votre connexion internet.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cet email est déjà utilisé par un compte existant. Impossible de créer un nouveau compte d\'authentification.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'L\'adresse email est invalide. Veuillez vérifier le format de l\'email.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.';
      } else if (error.message && error.message.includes('Erreur lors de la création du compte')) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(''), 7000);
    } finally {
      setLoading(false);
      setSelectedAdminForApproval(null);
      setAdminPosition('');
    }
  };
  
  // Fonction pour rejeter une demande d'administrateur
  const rejectAdminRequest = async (requestId) => {
    try {
      setLoading(true);
      
      // Supprimer la demande de la collection admin_requests
      await deleteDoc(doc(db, 'admin_requests', requestId));
      
      // Mettre à jour la liste des demandes
      setAdminRequests(prevRequests => 
        prevRequests.filter(req => req.id !== requestId)
      );
      
      setSuccessMessage('La demande a été rejetée.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Erreur lors du rejet de la demande:', error);
      setError('Une erreur est survenue lors du rejet de la demande.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Rendu du contenu en fonction de la section active
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <Dashboard 
            stats={stats} 
            adminCount={stats.adminCount} 
            loading={loading} 
            error={error} 
            successMessage={successMessage} 
            setActiveSection={setActiveSection}
          />
        );
        
      case 'admin-requests':
        return (
          <AdminRequests 
            adminRequests={adminRequests} 
            loading={loading} 
            error={error} 
            successMessage={successMessage} 
            openPositionDialog={openPositionDialog} 
            rejectAdminRequest={rejectAdminRequest} 
          />
        );
      
      case 'pending':
        return <PendingFarmers />;
      
      case 'administrators':
        return <Administrators />;
      
      case 'farmers':
        return <Farmers />;
      
      case 'announcements': // Nouveau cas pour les annonces
        return <Announcements />;
      
      case 'projects':
        return <Projects />;
      
      case 'news':
        return <News />;
      
      case 'statistics':
        return <Statistics />;
      
      case 'settings':
        return <Settings />;

      case 'diagnostics':
        return <Diagnostics initialSelectedId={selectedDiagnosticId} />;

      case 'newsletter':
        return <Newsletter />;
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* TopBar pour toutes les tailles d'écran */}
      <TopBar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection} 
        handleLogout={handleLogout} 
      />
      
      <Box sx={{ display: 'flex', flexGrow: 1, pt: '64px' }}> {/* Ajouter un padding-top pour éviter que le contenu soit caché par la TopBar */}
        {/* Sidebar pour desktop */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          handleLogout={handleLogout} 
        />
        
        {/* Main content */}
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, sm: 3 }, // Padding réduit sur mobile
            width: '100%',
            ml: { sm: '240px' }, // Marge à gauche pour compenser la Sidebar sur desktop
            mt: { xs: 2, sm: 0 } // Marge supérieure sur mobile
          }}
        >
          {renderContent()}
        </Box>
      </Box>

      {/* Dialogue pour saisir le poste de l'administrateur avant approbation */}
      <Dialog open={positionDialogOpen} onClose={() => setPositionDialogOpen(false)}>
        <DialogTitle>Définir le poste de l'administrateur</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Veuillez renseigner le poste de {selectedAdminForApproval?.name} avant de l'approuver en tant qu'administrateur.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="adminPosition"
            label="Poste de l'administrateur *"
            type="text"
            fullWidth
            variant="outlined"
            value={adminPosition}
            onChange={(e) => setAdminPosition(e.target.value)}
            required
            error={adminPosition.trim() === ''}
            helperText={adminPosition.trim() === '' ? 'Le poste est obligatoire' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPositionDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button 
            onClick={approveAdminRequest} 
            color="success" 
            variant="contained"
            disabled={adminPosition.trim() === ''}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
